import { Component, computed, inject } from '@angular/core';
import { COMMON_IMPORTS } from '../../../../Shared/common-imports';
import { AuthService } from '../../../../Shared/Services/auth.service';
import { PERMISSIONS } from '../../../../../Enums/permission.enum';
import { AnomaliesReportFacade } from '../../../../../Facades/reports/anomalies.facade';
import { EntraAdminRolesReportFacade } from '../../../../../Facades/reports/entra-admin-roles.facade';
import { M365LicenseReportFacade } from '../../../../../Facades/reports/m365-license.facade';
import { EM_DASH } from '../../../Consts/em-dash.const';
import { ReportOverviewCard } from '../../../Interfaces/report-overview-card.interface';

@Component({
  selector: 'app-reports-overview',
  standalone: true,
  imports: [COMMON_IMPORTS],
  templateUrl: './reports-overview.component.html',
  styleUrl: './reports-overview.component.scss',
})
export class ReportsOverviewComponent {
  private readonly authService = inject(AuthService);
  private readonly user = this.authService.getCurrentUser();

  readonly anomalies = this.user?.hasPermission(PERMISSIONS.ANOMALIES_REPORT)
    ? inject(AnomaliesReportFacade)
    : null;
  readonly entraAdminRoles = this.user?.hasPermission(PERMISSIONS.ENTRA_ADMIN_REPORT)
    ? inject(EntraAdminRolesReportFacade)
    : null;
  readonly m365License = this.user?.hasPermission(PERMISSIONS.M365_LICENSE_REPORT)
    ? inject(M365LicenseReportFacade)
    : null;

  readonly cards = computed<ReportOverviewCard[]>(() => this.buildCards());
  readonly hasCards = computed(() => this.cards().length > 0);

  private buildCards(): ReportOverviewCard[] {
    const cards: ReportOverviewCard[] = [];
    if (this.anomalies) cards.push(this.buildAnomaliesCard(this.anomalies));
    if (this.entraAdminRoles) cards.push(this.buildEntraAdminRolesCard(this.entraAdminRoles));
    if (this.m365License) cards.push(this.buildM365LicenseCard(this.m365License));
    return cards;
  }

  private buildAnomaliesCard(facade: AnomaliesReportFacade): ReportOverviewCard {
    return {
      key: 'anomalies_report',
      icon: 'bar_chart',
      accent: 'info',
      titleKey: 'app.navigation.secondary_nav.item.anomalies_report.title',
      descriptionKey: 'app.navigation.secondary_nav.item.anomalies_report.description',
      metricLabelKey: 'app.reports.dashboard.health_score',
      metricValue: () => this.formatMetric(facade.healthScore()),
      isLoading: () => facade.isLoading(),
      hasError: () => this.facadeHasError(facade.error()),
      routerLink: ['/reports', 'anomalies'],
    };
  }

  private buildEntraAdminRolesCard(facade: EntraAdminRolesReportFacade): ReportOverviewCard {
    return {
      key: 'entra_admin_roles_report',
      icon: 'admin_panel_settings',
      accent: 'warning',
      titleKey: 'app.navigation.secondary_nav.item.entra_admin_roles_report.title',
      descriptionKey: 'app.navigation.secondary_nav.item.entra_admin_roles_report.description',
      metricLabelKey: 'app.reports.dashboard.privileged_identities',
      metricValue: () => this.formatMetric(facade.privilegedIdentityCount()),
      isLoading: () => facade.isLoading(),
      hasError: () => this.facadeHasError(facade.error()),
      routerLink: ['/reports', 'entra-admin-roles'],
    };
  }

  private buildM365LicenseCard(facade: M365LicenseReportFacade): ReportOverviewCard {
    return {
      key: 'm365_license_assignment_report',
      icon: 'badge',
      accent: 'success',
      titleKey: 'app.navigation.secondary_nav.item.m365_license_assignment_report.title',
      descriptionKey: 'app.navigation.secondary_nav.item.m365_license_assignment_report.description',
      metricLabelKey: 'app.reports.m365_license.utilization',
      metricValue: () => this.formatUtilizationPercent(facade.utilization()),
      isLoading: () => facade.isLoading(),
      hasError: () => this.facadeHasError(facade.error()),
      routerLink: ['/reports', 'm365-license-assignment'],
    };
  }

  private formatMetric(value: number | null): string {
    return value === null ? EM_DASH : String(value);
  }

  private formatUtilizationPercent(utilization: number | null): string {
    return utilization === null ? EM_DASH : `${Math.round(utilization * 100)}%`;
  }

  private facadeHasError(error: unknown): boolean {
    return error !== undefined && error !== null;
  }
}
